import { useMemo, useState, useCallback } from 'react';
import './Projects.css';
import TechIcon from '../../components/TechIcon';
import portfolioData from '../../data/portfolio.json';

const projects = portfolioData;

function hasCoverImage(cover) {
  return cover !== null && cover !== undefined && cover !== '';
}

const COVER_EXT_TRY = ['.jpg', '.png'];

function ProjectCoverImg({ cover, alt, className }) {
  const stem = String(cover);
  const hasExplicitExt = /\.(png|jpe?g|webp)$/i.test(stem);
  const [extIdx, setExtIdx] = useState(0);

  const src = hasExplicitExt
    ? `/images/${stem}`
    : `/images/${stem}${COVER_EXT_TRY[extIdx]}`;

  const onError = useCallback(() => {
    if (hasExplicitExt) return;
    if (extIdx < COVER_EXT_TRY.length - 1) setExtIdx((i) => i + 1);
  }, [hasExplicitExt, extIdx]);

  return <img className={className} src={src} alt={alt} onError={onError} />;
}

export default function Projects() {
  const [filter, setFilter] = useState('All');
  const types = useMemo(() => {
    const unique = [...new Set(projects.map(p => p.type))].sort((a, b) => a.localeCompare(b));
    return ['All', ...unique];
  }, []);

  const visible = filter === 'All'
    ? projects
    : projects.filter(p => p.type === filter);

  return (
    <section id="projects">
      <div className="container">
        <div className="projects-header">
          <div>
            <p className="section-label">Work</p>
            <h2 className="section-title" style={{ marginBottom: 0 }}>Selected Projects</h2>
          </div>
          <div className="proj-filters">
            {types.map(t => (
              <button
                key={t}
                className={`proj-filter-btn${filter === t ? ' active' : ''}`}
                onClick={() => setFilter(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="projects-grid">
          {visible.map(p => (
            <div key={p.id} className="proj-card">
              {hasCoverImage(p.cover) && (
                <ProjectCoverImg className="proj-card-img" cover={p.cover} alt={p.name} />
              )}
              {!hasCoverImage(p.cover) && (
                <div className="proj-card-img" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(123,97,255,0.06) 100%)',
                  height: 180,
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  color: 'var(--text-3)',
                  letterSpacing: '0.1em',
                }}>
                  {p.type.toUpperCase()}
                </div>
              )}
              <div className="proj-card-body">
                <div className="proj-card-top">
                  <div className="proj-card-title">{p.name}</div>
                  <div className="proj-card-links">
                    {p.github && (
                      <a href={p.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                      </a>
                    )}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" aria-label="Live site">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                      </a>
                    )}
                  </div>
                </div>
                <p className="proj-card-desc">{p.about}</p>
                <div className="proj-card-tags">
                  {p.skills.map(s => (
                    <span key={s} className="proj-tag">
                      <TechIcon name={s} size={11} />
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
